import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from '../../../statspro/js/components/common/stats-section';
import { navSelector } from 'store/modules/competition-nav/selectors/nav-selector';

import {
    RoundSelectNavItem,
    CompNavItem,
    ResponsiveSeasonNavItem,
    ResponsiveRoundNavItem,
    LiveToggleNavItem
} from 'common/react/components/competition-nav/nav-items';

import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useNavSync } from 'common/react/components/competition-nav/hooks/use-nav-sync';
import { useWindowWidth } from 'common/react/hooks/use-window-resize';
import { useGetCurrentCompFilters } from 'common/react/components/competition-nav/hooks/use-get-current-comp-filters';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { FilterDropdown } from 'widgets/filters/js/components/FilterDropdown';
import { FilterButton } from 'common/react/components/FilterButton';
import { UPDATE_NAV_ITEM } from 'common/store/modules/competition-nav/actions';
import { getTranslation } from 'common/react/utils/translations';

/**
 * @constant {Array} Competitions we want to appear in the filter for the ladder
 */
export const ALLOWED_COMPS = window.LADDER_DEFAULT_COMP_ORDER
    ? window.LADDER_DEFAULT_COMP_ORDER.split(',')
    : [
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.WOMENS,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.PRE_SEASON,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.PREMIERSHIP,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.VFL_PREMIERSHIP,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.VFL_WOMENS,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.SANFL_PREMIERSHIP,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.U18_BOYS_CHAMPIONSHIPS,
          PULSE.app.common.CONSTANTS.PROVIDER_IDS.U18_GIRLS_CHAMPIONSHIPS
      ];

export const LadderNav = ({
    competitionId,
    seasonId,
    competitionProviderId,
    seasonPid,
    title
}) => {
    const windowWidth = useWindowWidth();
    const dispatch = useDispatch();

    const isResponsive = () => windowWidth <= PULSE.app.measurements.desktop;
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);
    const [resetFilterBtnStyles, setResetFilterBtnStyles] = useState(false);
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowWidth]);

    const initialValues = {
        competitionId,
        seasonId,
        competitionProviderId,
        seasonPid
    };
    const nav = useSelector(navSelector);
    const options = useCompetitionNavigationOptions(nav);

    useNavSync({ ...options, initialValues, removeFinals: true });

    const competitions = options.competitions;

    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );

    const currentFilters = useGetCurrentCompFilters();
    const competitionNavRef = useRef();

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: nav,
        options,
        filtersFields: ['competition', 'season', 'round'],
        filtersRef: competitionNavRef
    });

    const hasRoundChanged = useCallback(() => {
        if (!nav?.round) {
            return false;
        }

        return nav?.round?.id !== nav?.round?.initialItemId;
    }, [nav?.round]);

    const isSeasonChanged = useCallback(() => {
        if (!nav?.season) {
            return false;
        }

        return (
            parseInt(nav?.season?.id, 10) !==
            parseInt(nav?.season?.initialItemId, 10)
        );
    }, [nav?.season]);

    /**
     * Checks if any navigation filters have changed from their initial default values.
     *
     * @returns {boolean} True if any filter has changed from defaults
     */
    const hasChangedFromDefaults = useCallback(() => {
        if (!nav?.competition || !nav?.season || !nav?.round) {
            return false;
        }
        const isCompetitionChanged =
            nav?.competition?.id !== nav?.competition?.initialItemId;

        return isCompetitionChanged || isSeasonChanged() || hasRoundChanged();
    }, [nav?.competition, nav?.season, nav?.round]);

    /**
     * Resets all navigation filters (competition, season, round) back to their initial default values.
     */
    const resetToDefaults = () => {
        const initialCompetitionId = nav?.competition?.initialItemId || '';
        const initialSeasonId = nav?.season?.initialItemId || '';
        const initialRoundId = nav?.round?.initialItemId || '';

        if (nav?.competition?.id !== initialCompetitionId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'competition',
                    value: { id: initialCompetitionId }
                })
            );
        }
        if (nav?.season?.id !== initialSeasonId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'season',
                    value: { id: initialSeasonId }
                })
            );
        }
        if (nav?.round?.id !== initialRoundId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'round',
                    value: { id: initialRoundId }
                })
            );
        }
    };

    const renderMobile = () => (
        <>
            <StatsSection.NavItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    allowListOrder={ALLOWED_COMPS}
                    title={title}
                    hideLabel
                />
                <FilterButton
                    classes="fixtures-nav__filter-button"
                    onChange={() => setAreFilterOptionsVisible((prev) => !prev)}
                    areFilterOptionsVisible={areFilterOptionsVisible}
                    resetFilterBtnStyles={resetFilterBtnStyles}
                    setResetFilterBtnStyles={setResetFilterBtnStyles}
                    showFilterLabel={false}
                    showActiveCircle
                    isFilterSelectedCustom={() =>
                        isSeasonChanged() || hasRoundChanged()
                    }
                />
                <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                    <SettingsButton />
                </StatsSection.SettingsButton>
            </StatsSection.NavItems>
            {areFilterOptionsVisible && (
                <StatsSection.NavFilterItems hideReset>
                    <ResponsiveSeasonNavItem
                        seasons={seasons}
                        type="with-default"
                        title={title}
                    />
                    <ResponsiveRoundNavItem
                        removeFinals
                        selector={navSelector}
                        hideLabel
                    />
                    {hasChangedFromDefaults() ? (
                        <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                            <button
                                className="stats-leaders-filter-reset-button"
                                onClick={resetToDefaults}
                            >
                                {getTranslation(
                                    'label.statsLeaders.filters.resetButtonText'
                                )}
                            </button>
                        </div>
                    ) : null}
                </StatsSection.NavFilterItems>
            )}
            {showResponsiveItems && currentFilters?.round?.providerId && (
                <StatsSection.ResponsiveRowItems>
                    <LiveToggleNavItem
                        roundId={currentFilters.round.providerId}
                        labelRightPosition
                    />
                </StatsSection.ResponsiveRowItems>
            )}
        </>
    );

    const renderDesktop = () => (
        <>
            <StatsSection.NavItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    allowListOrder={ALLOWED_COMPS}
                    title={title}
                    hideLabel
                />
                <FilterDropdown
                    title={title}
                    icon="season-trophy"
                    navItem="season"
                    items={seasons}
                />
                <RoundSelectNavItem
                    removeFinals={true}
                    classes={'u-margin-right-auto'}
                    title={title}
                />
                {hasChangedFromDefaults() ? (
                    <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                        <button
                            className="stats-leaders-filter-reset-button"
                            onClick={resetToDefaults}
                        >
                            {getTranslation(
                                'label.statsLeaders.filters.resetButtonText'
                            )}
                        </button>
                    </div>
                ) : null}
            </StatsSection.NavItems>
            <StatsSection.NavFilterItems
                title={title}
                filterKeys={['competition', 'season', 'round']}
            ></StatsSection.NavFilterItems>
            <StatsSection.SettingsButton>
                {!showResponsiveItems && currentFilters?.round?.providerId && (
                    <LiveToggleNavItem
                        roundId={currentFilters.round.providerId}
                        classes={
                            'competition-nav__live-toggle-wrapper competition-nav__live-toggle-wrapper--ladder'
                        }
                    />
                )}
                <SettingsButton />
            </StatsSection.SettingsButton>
        </>
    );

    return (
        <div
            className="competition-nav filter-dropdown rounded-filter-wrapper"
            ref={competitionNavRef}
        >
            <StatsSection.Nav
                showResponsiveItems={showResponsiveItems}
                withScrollableFilter={!showResponsiveItems}
            >
                {showResponsiveItems ? renderMobile() : renderDesktop()}
            </StatsSection.Nav>
        </div>
    );
};

LadderNav.propTypes = {
    competitionId: PropTypes.string,
    competitionProviderId: PropTypes.string,
    seasonPid: PropTypes.string,
    seasonId: PropTypes.string,
    title: PropTypes.string
};
